import {Check, Plus, Users} from 'lucide-react'
import {useI18n} from '@/i18n'
import {Badge} from '@/components/ui/badge'
import {Button} from '@/components/ui/button'
import {Card, CardContent, CardHeader, CardTitle} from '@/components/ui/card'
import {IconText} from '@/components/shared/IconText'
import {InlineRow} from '@/components/shared/InlineRow'
import {MutedText} from '@/components/shared/MutedText'
import {Strong} from '@/components/shared/Strong'
import type {PeerAccessProfile} from '@/types/access-request'

interface PeerRecommendationListProps {
    peers: PeerAccessProfile[]
    isAlreadySelected: (peer: PeerAccessProfile) => boolean
    onAdd: (peer: PeerAccessProfile) => void
}

export function PeerRecommendationList({peers, isAlreadySelected, onAdd}: PeerRecommendationListProps) {
    const i18n = useI18n()
    return (
        <Card data-testid="peer-recommendation-list">
            <CardHeader className="pb-2">
                <CardTitle className="text-sm" data-testid="peer-recommendation-list-title">
                    <IconText icon={Users} size="sm">
                        {i18n.accessRequests.peerRecommendations}
                    </IconText>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="border rounded-lg max-h-48 overflow-y-auto" data-testid="peer-recommendation-list-items">
                    {peers.map(peer => (
                        <PeerRecommendationRow
                            key={peer.accessProfile.id}
                            peer={peer}
                            disabled={isAlreadySelected(peer)}
                            onAdd={() => onAdd(peer)}
                        />
                    ))}
                </div>
            </CardContent>
        </Card>
    )
}

interface PeerRecommendationRowProps {
    peer: PeerAccessProfile
    disabled: boolean
    onAdd: () => void
}

function PeerRecommendationRow({peer, disabled, onAdd}: PeerRecommendationRowProps) {
    const i18n = useI18n()
    return (
        <div className="hover:bg-muted border-b py-1.5 px-3"
             data-testid={`peer-recommendation-row-${peer.accessProfile.id}`}>
            <InlineRow justify="between">
                <InlineRow gap="sm" className="min-w-0">
                    <Strong truncate>{peer.accessProfile.displayName}</Strong>
                    <MutedText size="xs" className="shrink-0">{peer.resource.displayName}</MutedText>
                    <Badge variant="secondary" className="text-xs shrink-0">
                        {i18n.accessRequests.popularInDepartment({count: peer.peerCount})}
                    </Badge>
                </InlineRow>
                <Button size="sm"
                        variant={disabled ? 'secondary' : 'outline'}
                        disabled={disabled}
                        className="shrink-0 ml-2"
                        onClick={onAdd}
                        aria-label={disabled
                            ? `${peer.accessProfile.displayName} already added`
                            : `Add ${peer.accessProfile.displayName}`}
                        data-testid={`btn-add-peer-profile-${peer.accessProfile.id}`}>
                    {disabled ? <Check className="h-3 w-3"/> : <Plus className="h-3 w-3"/>}
                </Button>
            </InlineRow>
        </div>
    )
}
